import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, Bug, RefreshCw, Loader2, Copy, Trash2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { useAuthedServerFn } from "@/lib/server-fn-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { verifyIsAdmin } from "@/lib/admin.functions";
import {
  listBugReports,
  getBugReport,
  updateBugReport,
  deleteBugScreenshot,
  listBugReportMessagesAdmin,
  addBugReportMessage,
  updateBugReportMessage,
  deleteBugReportMessage,
  type BugReportListItem,
  type BugReportDetail,
  type AdminBugMessage,
} from "@/lib/bug-reports-admin.functions";

export const Route = createFileRoute("/_app/admin/bugs")({
  component: AdminBugsPage,
  head: () => ({ meta: [{ title: "Bug reports — Admin" }] }),
});

const STATUS_LABELS: Record<string, string> = {
  open: "Open",
  investigating: "Investigating",
  fixed: "Fixed",
  wont_fix: "Won't fix",
  duplicate: "Duplicate",
};

function statusColor(s: string) {
  if (s === "open") return "bg-amber-500/15 text-amber-700 border-amber-500/30 dark:text-amber-300";
  if (s === "investigating") return "bg-blue-500/15 text-blue-700 border-blue-500/30 dark:text-blue-300";
  if (s === "fixed") return "bg-emerald-500/15 text-emerald-700 border-emerald-500/30 dark:text-emerald-300";
  return "bg-muted text-muted-foreground border-border";
}

function AdminBugsPage() {
  const { session, loading } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const verifyFn = useAuthedServerFn(verifyIsAdmin);
  const listFn = useAuthedServerFn(listBugReports);
  const getFn = useAuthedServerFn(getBugReport);
  const updateFn = useAuthedServerFn(updateBugReport);
  const deleteShotFn = useAuthedServerFn(deleteBugScreenshot);

  const [entries, setEntries] = useState<BugReportListItem[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [status, setStatus] = useState<string>("__all");
  const [search, setSearch] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<BugReportDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (!session) {
      setIsAdmin(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await verifyFn();
        if (!cancelled) setIsAdmin(r.isAdmin);
      } catch {
        if (!cancelled) setIsAdmin(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const r = await listFn({
        data: { status: status as never, search: search.trim() || undefined, limit: 50, offset: 0 },
      });
      setEntries(r.entries);
      setCounts(r.counts);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setRefreshing(false);
    }
  }, [listFn, status, search]);

  useEffect(() => {
    if (isAdmin) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, status]);

  const loadDetail = useCallback(
    async (id: string) => {
      setSelectedId(id);
      setDetail(null);
      setDetailLoading(true);
      try {
        const d = await getFn({ data: { id } });
        setDetail(d);
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Failed to load report");
      } finally {
        setDetailLoading(false);
      }
    },
    [getFn],
  );

  if (loading || (session && isAdmin === null)) {
    return <div className="p-12 text-center text-muted-foreground">Loading…</div>;
  }
  if (!isAdmin) {
    return (
      <main className="max-w-md mx-auto px-4 py-16 text-center">
        <h1 className="font-display text-2xl font-bold mb-2">Admins only</h1>
        <Button asChild variant="outline">
          <Link to="/home">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
        </Button>
      </main>
    );
  }

  return (
    <main className="max-w-6xl mx-auto px-4 py-8">
      <header className="mb-6">
        <Link to="/admin" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2">
          <ArrowLeft className="w-3 h-3" /> Back to admin
        </Link>
        <h1 className="font-display text-3xl font-bold flex items-center gap-2">
          <Bug className="w-6 h-6 text-primary" /> Bug reports
        </h1>
        <div className="mt-2 flex flex-wrap gap-2 text-xs">
          {Object.entries(STATUS_LABELS).map(([k, label]) => (
            <span key={k} className={`px-2 py-0.5 rounded border ${statusColor(k)}`}>
              {label}: {counts[k] ?? 0}
            </span>
          ))}
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-4">
        {/* List */}
        <section className="rounded-xl border border-border bg-card p-4">
          <div className="flex gap-2 mb-3">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All statuses</SelectItem>
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              onBlur={load}
              placeholder="Search…"
              className="h-8 text-xs"
            />
            <Button variant="outline" size="sm" onClick={load} disabled={refreshing}>
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            </Button>
          </div>
          {entries.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">No reports.</div>
          ) : (
            <div className="space-y-1 max-h-[70vh] overflow-y-auto">
              {entries.map((e) => (
                <button
                  key={e.id}
                  onClick={() => loadDetail(e.id)}
                  className={`w-full text-left rounded-md border p-2 text-xs hover:bg-muted/40 transition-colors ${
                    selectedId === e.id ? "border-primary/50 bg-primary/5" : "border-border bg-card"
                  }`}
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className={`px-1.5 py-0 rounded border text-[10px] ${statusColor(e.status)}`}>
                      {STATUS_LABELS[e.status]}
                    </span>
                    {e.source === "error_boundary" && (
                      <span className="px-1.5 py-0 rounded text-[10px] bg-destructive/10 text-destructive border border-destructive/30">
                        crash
                      </span>
                    )}
                    {e.has_screenshot && <ImageIcon className="w-3 h-3 text-muted-foreground" />}
                    <span className="ml-auto text-[10px] text-muted-foreground">
                      {new Date(e.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="font-medium truncate">
                    {e.description || e.error_message || "(no description)"}
                  </div>
                  <div className="text-[10px] text-muted-foreground truncate">
                    {e.user_email_snapshot}
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Detail */}
        <section className="rounded-xl border border-border bg-card p-4 min-h-[300px]">
          {!selectedId ? (
            <div className="p-12 text-center text-sm text-muted-foreground">
              Select a report to view details.
            </div>
          ) : detailLoading || !detail ? (
            <div className="flex items-center justify-center gap-2 p-12 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : (
            <BugDetailView
              detail={detail}
              onUpdate={async (status, notes) => {
                try {
                  await updateFn({ data: { id: detail.id, status: status as never, admin_notes: notes } });
                  toast.success("Updated");
                  await loadDetail(detail.id);
                  await load();
                } catch (e) {
                  toast.error(e instanceof Error ? e.message : "Update failed");
                }
              }}
              onDeleteScreenshot={async () => {
                try {
                  await deleteShotFn({ data: { id: detail.id } });
                  toast.success("Screenshot deleted");
                  await loadDetail(detail.id);
                } catch (e) {
                  toast.error(e instanceof Error ? e.message : "Delete failed");
                }
              }}
            />
          )}
        </section>
      </div>
    </main>
  );
}

function BugDetailView({
  detail,
  onUpdate,
  onDeleteScreenshot,
}: {
  detail: BugReportDetail;
  onUpdate: (status: string, notes: string | null) => Promise<void>;
  onDeleteScreenshot: () => Promise<void>;
}) {
  const [status, setStatus] = useState(detail.status);
  const [notes, setNotes] = useState(detail.admin_notes ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setStatus(detail.status);
    setNotes(detail.admin_notes ?? "");
  }, [detail.id, detail.status, detail.admin_notes]);

  const copyMarkdown = () => {
    const lines = [
      `## Bug #${detail.id.slice(0, 8)}`,
      `**Submitted:** ${new Date(detail.created_at).toISOString()} by ${detail.user_email_snapshot ?? detail.user_id}`,
      `**Source:** ${detail.source}`,
      `**Status:** ${detail.status}`,
      detail.description ? `\n### Description\n${detail.description}` : "",
      `\n### Context`,
      `- **URL:** ${detail.url ?? "—"}`,
      `- **Route:** ${detail.route_pattern ?? "—"}`,
      `- **Viewport:** ${detail.viewport ?? "—"}`,
      `- **User agent:** ${detail.user_agent ?? "—"}`,
      `- **App version:** ${detail.app_version ?? "—"}`,
      detail.error_message ? `\n### Error\n${detail.error_message}\n\n\`\`\`\n${detail.error_stack ?? ""}\n\`\`\`` : "",
      detail.console_buffer.length
        ? `\n### Console buffer (last ${detail.console_buffer.length})\n` +
          detail.console_buffer
            .map((c: BugReportDetail["console_buffer"][number]) => `[${c.ts}] ${c.level}: ${c.message}`)
            .join("\n")
        : "",
      detail.admin_notes ? `\n### Admin notes\n${detail.admin_notes}` : "",
    ]
      .filter(Boolean)
      .join("\n");
    void navigator.clipboard.writeText(lines);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="space-y-4 text-sm">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <div className="font-mono text-xs text-muted-foreground">
            #{detail.id.slice(0, 8)} · {new Date(detail.created_at).toLocaleString()}
          </div>
          <div className="text-xs text-muted-foreground">{detail.user_email_snapshot}</div>
        </div>
        <Button size="sm" variant="outline" onClick={copyMarkdown} className="gap-1">
          <Copy className="w-3.5 h-3.5" /> Copy for troubleshooting
        </Button>
      </div>

      {detail.description && (
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">Description</div>
          <div className="rounded-md border border-border bg-muted/30 p-2 whitespace-pre-wrap">{detail.description}</div>
        </div>
      )}

      {detail.error_message && (
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-destructive mb-1">Error</div>
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-2 font-mono text-xs">
            {detail.error_message}
            {detail.error_stack && (
              <pre className="mt-2 max-h-60 overflow-auto text-[10px] opacity-80">{detail.error_stack}</pre>
            )}
          </div>
        </div>
      )}

      {detail.screenshot_signed_url && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Screenshot</div>
            <button onClick={onDeleteScreenshot} className="text-xs text-destructive hover:underline inline-flex items-center gap-1">
              <Trash2 className="w-3 h-3" /> Delete
            </button>
          </div>
          <a href={detail.screenshot_signed_url} target="_blank" rel="noreferrer">
            <img src={detail.screenshot_signed_url} alt="Screenshot" className="max-w-full rounded-md border border-border" />
          </a>
        </div>
      )}

      <details className="rounded-md border border-border bg-muted/20 p-2 text-xs">
        <summary className="cursor-pointer font-semibold">Context</summary>
        <dl className="mt-2 grid grid-cols-[max-content_1fr] gap-x-2 gap-y-1 font-mono text-[10px]">
          <dt className="text-muted-foreground">URL</dt><dd className="break-all">{detail.url ?? "—"}</dd>
          <dt className="text-muted-foreground">Route</dt><dd>{detail.route_pattern ?? "—"}</dd>
          <dt className="text-muted-foreground">Viewport</dt><dd>{detail.viewport ?? "—"}</dd>
          <dt className="text-muted-foreground">Version</dt><dd>{detail.app_version ?? "—"}</dd>
          <dt className="text-muted-foreground">User agent</dt><dd className="break-all">{detail.user_agent ?? "—"}</dd>
        </dl>
      </details>

      <details className="rounded-md border border-border bg-muted/20 p-2 text-xs">
        <summary className="cursor-pointer font-semibold">Console buffer ({detail.console_buffer.length})</summary>
        <pre className="mt-2 max-h-60 overflow-auto text-[10px] whitespace-pre-wrap">
          {detail.console_buffer
            .map((c: BugReportDetail["console_buffer"][number]) => `[${c.ts}] ${c.level}: ${c.message}`)
            .join("\n") || "(empty)"}
        </pre>
      </details>

      <div className="border-t border-border pt-3 space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Status</span>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-8 text-xs w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">
            Internal notes <span className="text-muted-foreground/70 normal-case font-normal">(never shown to reporter)</span>
          </div>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value.slice(0, 5000))}
            placeholder="Internal notes (optional)"
            rows={3}
            className="text-xs"
          />
        </div>
        <Button
          size="sm"
          disabled={saving || (status === detail.status && notes.trim() === (detail.admin_notes ?? "").trim())}
          onClick={async () => {
            setSaving(true);
            try {
              await onUpdate(status, notes.trim() || null);
            } finally {
              setSaving(false);
            }
          }}
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save status & notes"}
        </Button>
      </div>

      <MessagesToReporter bugReportId={detail.id} />
    </div>
  );
}

function MessagesToReporter({ bugReportId }: { bugReportId: string }) {
  const listMsgsFn = useAuthedServerFn(listBugReportMessagesAdmin);
  const addMsgFn = useAuthedServerFn(addBugReportMessage);
  const updateMsgFn = useAuthedServerFn(updateBugReportMessage);
  const deleteMsgFn = useAuthedServerFn(deleteBugReportMessage);

  const [messages, setMessages] = useState<AdminBugMessage[] | null>(null);
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState<string | null>(null);
  const [editBody, setEditBody] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await listMsgsFn({ data: { id: bugReportId } });
      setMessages(r.messages);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to load messages");
    }
  }, [listMsgsFn, bugReportId]);

  useEffect(() => {
    setMessages(null);
    setDraft("");
    setEditing(null);
    void load();
  }, [load]);

  const handleAdd = async () => {
    const body = draft.trim();
    if (body.length < 1) return;
    setBusy(true);
    try {
      await addMsgFn({ data: { bugReportId, body } });
      setDraft("");
      await load();
      toast.success("Message posted");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to send");
    } finally {
      setBusy(false);
    }
  };

  const handleSaveEdit = async (id: string) => {
    const body = editBody.trim();
    if (body.length < 1) return;
    setBusy(true);
    try {
      await updateMsgFn({ data: { id, body } });
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to update");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this message? The reporter will no longer see it.")) return;
    setBusy(true);
    try {
      await deleteMsgFn({ data: { id } });
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to delete");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-t border-border pt-3 space-y-2">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
        Messages to reporter
      </div>
      {messages === null ? (
        <div className="text-xs text-muted-foreground">Loading…</div>
      ) : messages.length === 0 ? (
        <div className="text-xs text-muted-foreground italic">No messages sent yet.</div>
      ) : (
        <div className="space-y-2">
          {messages.map((m: AdminBugMessage) => {
            const isDeleted = !!m.deleted_at;
            const isEditing = editing === m.id;
            return (
              <div
                key={m.id}
                className={`rounded-md border p-2 text-xs ${
                  isDeleted ? "border-dashed border-muted-foreground/30 bg-muted/20 opacity-60" : "border-primary/20 bg-primary/5"
                }`}
              >
                {isEditing ? (
                  <div className="space-y-2">
                    <Textarea
                      value={editBody}
                      onChange={(e) => setEditBody(e.target.value.slice(0, 5000))}
                      rows={3}
                      className="text-xs"
                    />
                    <div className="flex gap-2">
                      <Button size="sm" disabled={busy} onClick={() => handleSaveEdit(m.id)}>
                        Save
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditing(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="whitespace-pre-wrap">
                      {isDeleted ? <em>(deleted — no longer visible to reporter)</em> : m.body}
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-[10px] text-muted-foreground">
                      <span>{new Date(m.created_at).toLocaleString()}</span>
                      {m.updated_at !== m.created_at && <span>· edited</span>}
                      {!isDeleted && (
                        <>
                          <button
                            className="ml-auto hover:underline"
                            onClick={() => {
                              setEditing(m.id);
                              setEditBody(m.body);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="text-destructive hover:underline"
                            onClick={() => handleDelete(m.id)}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
      <Textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value.slice(0, 5000))}
        placeholder="Write a message to the reporter…"
        rows={3}
        className="text-xs"
      />
      <Button size="sm" disabled={busy || draft.trim().length < 1} onClick={handleAdd}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Send to reporter"}
      </Button>
    </div>
  );
}
